import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send } from "lucide-react";

export default function Chat() {
    const { user } = useAuth();
    const { id } = useParams();
    const nav = useNavigate();
    const [convs, setConvs] = useState([]);
    const [active, setActive] = useState(id || null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const scrollRef = useRef(null);

    const loadConvs = async () => {
        const { data } = await api.get("/conversations");
        setConvs(data.data || []);
        if (!active && data.data?.length) setActive(data.data[0].id);
    };
    const loadMsgs = async (cid) => {
        if (!cid) return;
        const { data } = await api.get(`/conversations/${cid}/messages`);
        setMessages(data.data || []);
        setTimeout(() => scrollRef.current?.scrollTo({ top: 99999 }), 50);
    };

    useEffect(() => { loadConvs(); }, []);
    useEffect(() => { if (active) { loadMsgs(active); nav(`/app/chat/${active}`, { replace: true }); } }, [active]);

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim() || !active) return;
        await api.post("/messages", { conversation_id: active, body: text.trim() });
        setText("");
        loadMsgs(active);
        loadConvs();
    };

    const activeConv = convs.find((c) => c.id === active);
    const otherName = (c) => user?.role === "professional" ? c.facility_name : c.professional_name;

    return (
        <div className="max-w-6xl mx-auto h-[calc(100vh-8rem)] grid grid-cols-12 gap-4">
            <div className="col-span-4 hl-card flex flex-col overflow-hidden">
                <div className="p-4 border-b border-[#E5E5E0]"><h2 className="font-display font-bold">Conversazioni</h2></div>
                <div className="flex-1 overflow-y-auto">
                    {convs.length === 0 && <div className="p-6 text-sm text-[#5C5C5C]">Nessuna conversazione. Candidati per iniziare.</div>}
                    {convs.map((c) => (
                        <button key={c.id} onClick={() => setActive(c.id)}
                            data-testid={`conv-${c.id}`}
                            className={`w-full text-left p-4 border-b border-[#E5E5E0] transition-colors ${active === c.id ? "bg-[#EEF3F1]" : "hover:bg-[#F9F9F7]"}`}>
                            <div className="font-semibold text-sm">{otherName(c)}</div>
                            <div className="text-xs text-[#5C5C5C] truncate mt-1">{c.job_title}</div>
                            {c.last_message && <div className="text-xs text-[#5C5C5C] truncate mt-1">{c.last_message}</div>}
                        </button>
                    ))}
                </div>
            </div>
            <div className="col-span-8 hl-card flex flex-col overflow-hidden">
                {!active && <div className="flex-1 grid place-items-center text-[#5C5C5C]">Seleziona una conversazione</div>}
                {active && (
                    <>
                        <div className="p-4 border-b border-[#E5E5E0]">
                            <div className="font-semibold">{activeConv ? otherName(activeConv) : ""}</div>
                            <div className="text-xs text-[#5C5C5C]">{activeConv?.job_title}</div>
                        </div>
                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                            {messages.map((m) => {
                                const mine = m.sender_id === user.id;
                                return (
                                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[70%] px-4 py-2 rounded-lg text-sm ${mine ? "bg-[#1C3F39] text-white" : "bg-[#F9F9F7] border border-[#E5E5E0]"}`}
                                            data-testid={`msg-${m.id}`}>
                                            <div>{m.body}</div>
                                            <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-[#5C5C5C]"}`}>
                                                {new Date(m.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <form onSubmit={send} className="p-4 border-t border-[#E5E5E0] flex gap-2">
                            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio…" data-testid="chat-input" />
                            <Button type="submit" className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="chat-send-btn"><Send className="w-4 h-4" /></Button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
